import { z } from "zod";
import {
  zWorldId,
  zWorldSourceRef,
  zWorldState,
  zWorldEvent,
  zWorldCommit,
  zContinuityDecision,
  zWorldProfile,
} from "./worlds";
import { zStoryCastMember, zPerformance, zStoryScene, zStoryBeatRecord } from "./performances";
import { zWorldPlace } from "./backdrops";
import { zStoryItemDefinition } from "./storyItems";

/** Accepted narrative history is authoritative. Snapshots and bounded evidence are rebuildable projections. */
// Unknown persisted metadata is ignored; required versions, IDs and relationships remain validated.
export const zWorldLedger = z
  .object({
    schemaVersion: z.literal(3),
    worldId: zWorldId,
    revision: z.number().int().nonnegative(),
    baseline: zWorldState,
    events: z.array(zWorldEvent),
    commits: z.array(zWorldCommit),
    decisions: z.array(zContinuityDecision),
    now: zWorldState.shape.frontier,
  })
  .strip();
export type WorldLedger = z.infer<typeof zWorldLedger>;
export const zWorldHistoryManifest = z
  .object({
    schemaVersion: z.literal(2),
    revision: z.number().int().nonnegative(),
    complete: z.literal(true),
    chunkIds: z.array(zWorldId).min(1),
  })
  .strip();
export const zWorldSourceManifest = z
  .object({
    schemaVersion: z.literal(2),
    sourceId: zWorldId,
    complete: z.literal(true),
    chunkIds: z.array(zWorldId).min(1),
    worldProtected: z.literal(true),
  })
  .strip();
export const zWorldSnapshotManifest = zWorldState
  .omit({ entities: true, facts: true, threads: true, items: true })
  .extend({ complete: z.literal(true), chunkIds: z.array(zWorldId).min(1) })
  .strip();
export const zWorldSourcePublication = z.object({ snapshotId: zWorldId }).strip();
export const zWorldEvidenceProjection = z
  .object({
    schemaVersion: z.literal(2),
    worldId: zWorldId,
    source: zWorldSourceRef,
    complete: z.literal(true),
    beatIds: z.array(zWorldId).max(20),
    quotes: z
      .array(
        z
          .object({
            beatId: zWorldId,
            sequence: z.number(),
            lines: z.array(
              z.object({ kind: z.enum(["say", "narrate"]), characterId: zWorldId.optional(), text: z.string() }).strip()
            ),
            itemCues: z.array(
              z
                .object({
                  action: z.string(),
                  itemId: zWorldId,
                  characterId: zWorldId,
                  toCharacterId: zWorldId.optional(),
                  abilityId: zWorldId.optional(),
                })
                .strip()
            ),
          })
          .strip()
      )
      .max(20),
    truncated: z.boolean(),
    publicationSnapshotId: zWorldId.optional(),
  })
  .strip();
export type WorldEvidenceProjection = z.infer<typeof zWorldEvidenceProjection>;
/** Resource records bind world identity to concrete account resources; names are presentation only. */
export const zWorldCharacter = z.object({ entityId: zWorldId, cast: zStoryCastMember }).passthrough();
export const zWorldThing = z.object({ entityId: zWorldId, item: zStoryItemDefinition }).passthrough();
export { zWorldPlace };

export const zWorldSceneCapturePayload = z
  .object({ performance: zPerformance, scene: zStoryScene, beats: z.array(zStoryBeatRecord) })
  .passthrough();
export type WorldSceneCapturePayload = z.infer<typeof zWorldSceneCapturePayload>;

export const zWorldEventReceipt = zWorldEvent.pick({
  id: true,
  worldId: true,
  description: true,
  source: true,
  anchor: true,
  participantIds: true,
  depictions: true,
  chronologyLinks: true,
  observations: true,
  portrays: true,
  threadProgress: true,
});
